import { Paragraph, ResponsiveButton, ShareIcon, TextInput, ToggleInput, notification } from "@datagame/ui";
import { useMemo } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { Section } from "../../../../project/ProjectView/ProjectSettings/Steps/Common";
import ShareImage from "../../../../../assets/images/share.svg";
import { ProjectForReport } from "../../ReportView";
import { PROJECT_PUBLIC } from "../../../../../graphql/queries/ProjectQueries";
import { PROJECT_UPDATE } from "../../../../../graphql/mutations/ProjectMutations";

import styles from "./Content.module.scss";

export interface ContentProps {
  project: ProjectForReport;
}

export const Content = ({ project }: ContentProps) => {
  const { t } = useTranslation();

  const [updateProject] = useMutation(PROJECT_UPDATE, {
    refetchQueries: [{ query: PROJECT_PUBLIC, variables: { projectId: project.id } }],
  });

  const handleChangeIsPublic = () => {
    updateProject({
      variables: {
        projectId: project.id,
        data: {
          public: !project.public,
        },
      },
    });
  };

  const copyLink = useMemo(() => {
    return `${window.location.origin}/public${window.location.pathname}`;
  }, []);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(copyLink);
    notification.info({
      title: t("report:shareSection.copyNotification.title"),
      description: t("report:shareSection.copyNotification.description"),
    });
  };

  return (
    <div className={styles.wrapper}>
      <Section title={t("report:preferences")} className={styles.section}>
        <div className={styles.publicDisplayContainer}>
          <div className={styles.publicDisplayHeader}>
            <Paragraph weight="font-bold">{t("report:allowPublicDisplay")}</Paragraph>
            <Paragraph color="secondary" size="small">
              {t("report:allowPublicDisplayDescription")}
            </Paragraph>
          </div>

          <ToggleInput name="public-display" value={project.public} onChange={handleChangeIsPublic} />
        </div>
      </Section>
      {project.public && (
        <Section title={t("report:share")} className={styles.section}>
          <div className={styles.shareContent}>
            <div className={styles.imageWrapper}>
              <img src={ShareImage} alt="share"></img>
            </div>

            <div className={styles.shareWrapper}>
              <div className={styles.shareWrapperContent}>
                <TextInput icon={<ShareIcon />} value={copyLink} readOnly size="small" />
                <ResponsiveButton
                  icon={<ShareIcon />}
                  iconSide="left"
                  onClick={handleCopyLink}
                  className={styles.copyButton}
                >
                  {t("projects:shareModal.copyLink")}
                </ResponsiveButton>
              </div>
              <Paragraph size="xs" className={styles.tip}>
                {t("report:shareSection.tip")}
              </Paragraph>
            </div>
          </div>
        </Section>
      )}
    </div>
  );
};
